import {
    DialogContent,
    TextField,
    Button,
    Grid,
    Stack,
    DialogTitle,
} from "@mui/material";
import { useState } from "react";
import TalentClient, { TalentErrors } from "../apiClients/TalentClient";
import { Talent } from '../models/dtos';
import { useErrorDisplay } from "../components/ErrorDisplay"
import { IsEmpty, IsNotEmpty, GetUpsertIcon, DefaultDateTimeOffset } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import PositionAutocompleteControl from '../components/PositionAutocompleteControl';
import ConditionalDisplay from "../components/ConditionalDisplay";
import TalentEventManagementComponent from "../ui-components/TalentEventManagementComponent";
import { I18N } from "../I18N";
import SystemUserAutocompleteControl from "../components/SystemUserAutocompleteControl";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, StickyDialogActions } from "../components/responsive";

export interface TalentUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    isBank?: boolean;
    talentId?: string;
    inTalent?: Talent;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const TalentUpsertPopup = ({ mode, isBank = false, talentId, inTalent, onChange = async () => { }, onAdd = async () => { }, icon = GetUpsertIcon(mode), ...btnProps }: TalentUpsertProps) => {

    const newTalent: Talent = {
        Id: '',
        OrganizationId: '',
        SystemUserId: '',
        PositionId: '',
        AssessorId: '',
        FirstName: '',
        LastName: '',
        Email: '',
        OrganizationIdentifier: '',
        IsActive: true,
        ActivatedDate: DefaultDateTimeOffset,
        DeactivatedDate: null,
        ActivationHistory: [],
        IsDeleted: false,
        LastUpdateDate: DefaultDateTimeOffset,
    }

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const { mode: viewportMode, isPhone, isTablet, isDesktop, isLandscape } = useViewportMode();
    const splitColumns = isDesktop || (isTablet && isLandscape);

    const [talent, setTalent] = useState<Talent>(newTalent);
    const [talentHasChanged, setTalentHasChanged] = useState<boolean>(false);

    const title = (mode === UpsertAction.Create) ? I18N.Translate('Create Talent') : I18N.Translate('Edit Talent');

    const disableSave = () => {
        if (mode === UpsertAction.Create) {
            return IsEmpty(talent?.FirstName) || IsEmpty(talent?.LastName) || IsEmpty(talent?.Email) || IsEmpty(talent?.PositionId);
        }
        else {
            return !talentHasChanged;
        }
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                await createTalent();
            }
            else if (mode === UpsertAction.Update) {

                await new TalentClient().Update(talent);
                setTalentHasChanged(false);
                await onChange();
            }

        } catch (error) {
            setError(error, TalentErrors.Update);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setTalent({ ...talent, [name]: value })
        setTalentHasChanged(true);
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const loadData = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                setTalent(newTalent);
            }
            else if (mode === UpsertAction.Update) {
                await refreshData();
            }

        } catch (error) {
            setError(error, TalentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const createTalent = async () => {
        let ct = {
            PositionId: talent.PositionId,
            FirstName: talent.FirstName,
            LastName: talent.LastName,
            Email: talent.Email,
            OrganizationIdentifier: talent.OrganizationIdentifier
        };

        let id = await new TalentClient().Create(ct);
        
        setOpen(false);
        await onAdd(id);
    }

    const handleUserSelect = (positionId) => {
        setTalent({ ...talent, PositionId: positionId })
        setTalentHasChanged(true);
    }

    const handleAssessorSelect = (positionId) => {
        setTalent({ ...talent, AssessorId: positionId })
        setTalentHasChanged(true);
    }

    const refreshData = async () => {
        let client = new TalentClient();

        let graph = await client.GetSingleGraph(talentId);
        let t = client.ExtractTalent(graph);
        setTalent(t);
    }


    return (
        <>
            <ErrorDisplay />
            <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

            <ResponsiveDialog open={open} fullWidth={true} maxWidth='lg'>
                <DialogTitle>
                    {title.trim()}
                </DialogTitle>
                <DialogContent dividers sx={{ px: isPhone ? 2 : 3, overflowY: 'auto' }}>
                    <BusySpinner busy={busy}>
                        <Grid container spacing={3} data-viewport-mode={viewportMode} sx={{ '& .MuiInputBase-root, & .MuiButtonBase-root': { minHeight: isDesktop ? undefined : 44 } }}>
                            {/* Talent Info */}
                            <Grid item xs={splitColumns ? 6 : 12}>
                                <Stack spacing={3}>
                                    <TextField label={I18N.Translate("First Name")} fullWidth
                                        name="FirstName"
                                        error={IsEmpty(talent?.FirstName)}
                                        size='small'
                                        variant="outlined"
                                        value={talent?.FirstName ?? ''}
                                        onChange={handleInputChange} />
                                    <TextField label={I18N.Translate("Last Name")} fullWidth
                                        name="LastName"
                                        error={IsEmpty(talent?.LastName)}
                                        size='small'
                                        variant="outlined"
                                        value={talent?.LastName ?? ''}
                                        onChange={handleInputChange} />
                                    <TextField label={I18N.Translate("Email")} fullWidth
                                        name="Email"
                                        error={IsEmpty(talent?.Email)}
                                        size='small'
                                        variant="outlined"
                                        value={talent?.Email ?? ''}
                                        onChange={handleInputChange} />
                                    <TextField label={I18N.Translate("Org Identifier")} fullWidth
                                        name="OrganizationIdentifier"
                                        size='small'
                                        variant="outlined"
                                        value={(IsNotEmpty(talent?.OrganizationIdentifier)) ? talent.OrganizationIdentifier : 'Not Assigned'}
                                        onChange={handleInputChange} />

                                    <PositionAutocompleteControl positionId={talent?.PositionId} onUserSelect={handleUserSelect} />
                                    <SystemUserAutocompleteControl systemUserId={talent?.AssessorId} placeholderText="Assign Assessor" onUserSelect={handleAssessorSelect} />
                                </Stack>
                            </Grid>

                            <Grid item xs={splitColumns ? 6 : 12}>
                                <ConditionalDisplay ifTrue={mode === UpsertAction.Update || mode === UpsertAction.View}>
                                    <TalentEventManagementComponent subscriptionActive={true} talentId={talent?.Id} />
                                </ConditionalDisplay>
                            </Grid>
                        </Grid>
                    </BusySpinner>
                </DialogContent>
                <StickyDialogActions phoneLayout='stacked' viewportOverride={viewportMode}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                    <Button disabled={disableSave()} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                </StickyDialogActions>
            </ResponsiveDialog>
        </>)
}

export default TalentUpsertPopup;
